import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, CheckCircle2, ScanLine, ShieldAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { inputClass } from "@/components/Panel";
import { Badge, Card } from "@/components/ui";
import { LabelButton } from "@/features/documents/LabelButton";
import { purchaseOrderDetailQuery, statusTone } from "@/features/purchase-orders/api";
import { ReceiveForm } from "@/features/purchase-orders/Receipts";
import {
  daysLate,
  isReceivingWindow,
  receivingQueueQuery,
  receivingTodayQuery,
  receivingWindows,
  type ReceivingOrder,
  type ReceivingWindow,
  type TodayReceipt,
} from "@/features/purchase-orders/receiving";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";

// Receiving workbench (PX-PUR-220, docs/design/workbench.md §2): what is due
// in, find the order from what lies on the dock (scan the PO number, the
// supplier's delivery note, a supplier or an item code), receive it with the
// order's receipt form, and see today's receipts with labels and the
// inspections they created. Round-trips: queue (1, debounced 200 ms, stale
// requests cancelled), today (1), the selected order's document (1); posting
// is post_purchase_receipt (1) and refreshes all three.

interface Search {
  q: string;
  days: ReceivingWindow;
  po?: string | undefined;
}

const UUID = /^[0-9a-f-]{36}$/i;

export const Route = createFileRoute("/_app/procurement/receiving")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const days = Number(raw.days);
    const s: Search = {
      q: typeof raw.q === "string" ? raw.q.slice(0, 60) : "",
      days: isReceivingWindow(days) ? days : 7,
    };
    if (typeof raw.po === "string" && UUID.test(raw.po)) s.po = raw.po;
    return s;
  },
  component: ReceivingPage,
});

function ReceivingPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { company } = useActiveCompany();
  const companyId = company?.id ?? "";
  const [term, setTerm] = useState(search.q);
  const [posted, setPosted] = useState<string | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);

  // Debounced: the URL (and the query key) follow the typed term.
  useEffect(() => {
    const id = setTimeout(() => {
      if (term.trim() !== search.q) void navigate({ search: (prev) => ({ ...prev, q: term.trim() }), replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [term, search.q, navigate]);

  const queue = useQuery(receivingQueueQuery(companyId, search.q, search.days));
  const today = useQuery(receivingTodayQuery(companyId));
  const orders = queue.data?.orders ?? [];
  const select = (id: string | undefined) => {
    setPosted(null);
    void navigate({ search: (prev) => ({ ...prev, po: id }), replace: true });
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4 pb-6" data-testid="receiving">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col">
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("rcv.title")}</h1>
          <span className="text-[13px] text-ink-muted">{t("rcv.intro")}</span>
        </div>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("rcv.window")}
          <select
            value={search.days}
            onChange={(e) => void navigate({ search: (prev) => ({ ...prev, days: Number(e.target.value) as ReceivingWindow }), replace: true })}
            className={cn(inputClass, "h-10 min-w-[160px] text-[14px]")}
          >
            {receivingWindows.map((d) => (
              <option key={d} value={d}>
                {d === 0 ? t("rcv.windowToday") : t("rcv.windowDays", { count: d })}
              </option>
            ))}
          </select>
        </label>
      </header>

      <div className="grid min-h-0 grow gap-4 lg:grid-cols-[minmax(320px,2fr)_minmax(0,3fr)]">
        <section aria-label={t("rcv.queue")} className="flex min-h-0 flex-col gap-2">
          <label className="relative flex flex-col gap-1 text-xs text-ink-muted">
            {t("rcv.scan")}
            <ScanLine size={16} className="pointer-events-none absolute bottom-3 left-3 text-ink-muted" aria-hidden="true" />
            <input
              ref={scanRef}
              // A scanner types and presses Enter: the desk starts here.
              autoFocus
              value={term}
              maxLength={60}
              placeholder={t("rcv.scanPlaceholder")}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setTerm("");
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  // One hit on the current term: open it.
                  const only = queue.data && search.q === term.trim() && orders.length === 1 ? orders[0] : undefined;
                  if (only) select(only.id);
                }
              }}
              className={cn(inputClass, "h-10 pl-9 text-[14px]")}
            />
          </label>
          <h2 className="flex items-center gap-1 text-[13px] font-semibold text-ink-soft">
            {search.q ? t("rcv.matches") : t("rcv.queue")}
            {queue.data && <span className="tabular text-ink-muted">· {orders.length}{queue.data.has_more ? "+" : ""}</span>}
          </h2>
          {!queue.data ? (
            <div className="flex flex-col gap-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[84px] animate-pulse rounded-2xl bg-surface-muted" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <Card className="flex flex-col gap-1 p-6 text-[13px] text-ink-muted">
              <span>{search.q ? t("rcv.noMatch", { term: search.q }) : t("rcv.empty")}</span>
              {queue.data.suppliers_too_broad && <span className="text-xs">{t("rcv.tooBroad")}</span>}
            </Card>
          ) : (
            <ul className={cn("flex flex-col gap-2 overflow-y-auto", queue.isFetching && "opacity-80")}>
              {orders.map((o) => (
                <li key={o.id}>
                  <OrderCard order={o} today={queue.data.today} selected={o.id === search.po} onSelect={() => select(o.id)} />
                </li>
              ))}
              {queue.data.has_more && <li className="px-1 text-xs text-ink-muted">{t("rcv.more")}</li>}
              {queue.data.suppliers_too_broad && <li className="px-1 text-xs text-ink-muted">{t("rcv.tooBroad")}</li>}
            </ul>
          )}
        </section>

        <div className="flex min-w-0 flex-col gap-4">
          <section aria-label={t("rcv.order")}>
            {posted && (
              <p role="status" className="mb-2 flex items-center gap-1.5 text-[13px] text-(--status-success-fg)">
                <CheckCircle2 size={14} aria-hidden="true" />
                {t("rcv.posted", { number: posted })}
              </p>
            )}
            {search.po ? (
              <OrderReceive
                id={search.po}
                onPosted={(number) => {
                  setPosted(number);
                  setTerm("");
                  scanRef.current?.focus();
                }}
                onClose={() => select(undefined)}
              />
            ) : (
              <Card className="flex min-h-[160px] items-center justify-center p-6 text-[13px] text-ink-muted">{t("rcv.pick")}</Card>
            )}
          </section>
          <TodayList receipts={today.data?.receipts} hasMore={today.data?.has_more ?? false} />
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order: o, today, selected, onSelect }: { order: ReceivingOrder; today: string; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string) => string;
  const fmt = useFormat();
  const late = daysLate(o.next_promised_date, today);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "flex w-full flex-col gap-1 rounded-2xl border bg-surface p-3 text-left transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-focus",
        selected ? "border-accent ring-1 ring-accent" : "border-border",
      )}
    >
      <span className="flex items-center gap-2">
        <span className="font-mono text-[13px] font-medium">{o.number}</span>
        {o.match && <Badge tone="info">{tr(`rcv.match.${o.match}`)}</Badge>}
        <span className="grow" />
        {o.next_promised_date &&
          (late > 0 ? (
            <Badge tone="danger">{t("rcv.overdue", { count: late })}</Badge>
          ) : late === 0 ? (
            <Badge tone="warning">{t("rcv.dueToday")}</Badge>
          ) : (
            <span className="tabular text-xs text-ink-muted">{fmt.day(o.next_promised_date)}</span>
          ))}
      </span>
      <span className="truncate text-[13px]">
        {o.supplier_name}
        {o.supplier_reference && <span className="text-ink-muted"> · {o.supplier_reference}</span>}
      </span>
      <span className="tabular flex gap-2 text-xs text-ink-muted">
        <span>{t("rcv.openLines", { open: o.open_lines, total: o.line_count })}</span>
        {o.received_lines > 0 && <span>· {t("rcv.partly", { count: o.received_lines })}</span>}
        {o.warehouse_code && <span>· {o.warehouse_code}</span>}
      </span>
    </button>
  );
}

function OrderReceive({ id, onPosted, onClose }: { id: string; onPosted: (number: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const canPost = useCan("procurement.receipt.post");
  const po = useQuery(purchaseOrderDetailQuery(id));
  if (!po.data) {
    return <div className="h-[280px] animate-pulse rounded-2xl bg-surface-muted" aria-hidden="true" />;
  }
  const d = po.data;
  const receivable = d.status === "released" || d.status === "partially_received";
  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="receiving-order">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[15px] font-semibold">{d.number}</span>
        <Badge tone={statusTone[d.status]}>{t(`status.${d.status}`)}</Badge>
        <span className="truncate text-[13px] text-ink-soft">{d.supplier_name}</span>
        <span className="grow" />
        <Link
          to="/procurement/purchase-orders"
          search={{ status: "all", q: "", po: d.id }}
          className="inline-flex items-center gap-1 text-xs font-medium text-accent-ink hover:underline"
        >
          {t("rcv.openOrder")}
          <ArrowUpRight size={12} aria-hidden="true" />
        </Link>
      </div>
      {!canPost ? (
        <p className="flex items-center gap-1.5 text-[13px] text-ink-muted">
          <ShieldAlert size={14} aria-hidden="true" />
          {t("rcv.noPermission")}
        </p>
      ) : !receivable ? (
        <p className="text-[13px] text-ink-muted">{t("rcv.notReceivable")}</p>
      ) : (
        // A new revision (after a receipt) starts a fresh form with the new remaining quantities.
        <ReceiveForm key={`${d.id}:${d.revision}`} po={d} onDone={(r) => onPosted(r.number)} onCancel={onClose} />
      )}
    </Card>
  );
}

function TodayList({ receipts, hasMore }: { receipts: TodayReceipt[] | undefined; hasMore: boolean }) {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string) => string;
  const fmt = useFormat();
  return (
    <section aria-label={t("rcv.today")} className="flex flex-col gap-2">
      <h2 className="flex items-center gap-1 text-[13px] font-semibold text-ink-soft">
        {t("rcv.today")}
        {receipts && <span className="tabular text-ink-muted">· {receipts.length}{hasMore ? "+" : ""}</span>}
        <span className="grow" />
        <Link to="/procurement/receipts" search={{ q: "" }} className="text-xs font-medium text-accent-ink hover:underline">
          {t("rcv.journal")}
        </Link>
      </h2>
      {!receipts ? (
        <div className="h-[72px] animate-pulse rounded-2xl bg-surface-muted" aria-hidden="true" />
      ) : receipts.length === 0 ? (
        <Card className="p-4 text-[13px] text-ink-muted">{t("rcv.todayEmpty")}</Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {receipts.map((r) => (
            <li key={r.id}>
              <Card className={cn("flex flex-col gap-1.5 p-3 text-[13px]", r.is_reversal && "bg-surface-muted")}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-medium">{r.number}</span>
                  <span className="tabular text-xs text-ink-muted">{fmt.time(r.posted_at)}</span>
                  {r.is_reversal && <Badge tone="neutral">{t("rcv.reversal")}</Badge>}
                  {r.reversed && <Badge tone="neutral">{t("rcv.reversed")}</Badge>}
                  <span className="grow" />
                  <span className="font-mono text-xs">{r.order_number}</span>
                  <span className="truncate text-xs text-ink-soft">{r.supplier_name}</span>
                </div>
                {(r.delivery_note || r.posted_by_name) && (
                  <div className="text-xs text-ink-muted">
                    {r.delivery_note && `${t("po.deliveryNote")}: ${r.delivery_note}`}
                    {r.delivery_note && r.posted_by_name && " · "}
                    {r.posted_by_name}
                  </div>
                )}
                <ul className="tabular flex flex-col gap-1 text-xs text-ink-soft">
                  {r.lines.map((l) => (
                    <li key={l.line_no} className="flex flex-wrap items-center gap-2">
                      <span className="font-mono">{l.item_code}</span>
                      <span>
                        {fmt.qty(String(l.qty))} {l.uom_code} → {l.location_code ?? "—"}
                      </span>
                      {l.lot_code && <span className="font-mono">{l.lot_code}</span>}
                      {l.lot_id && l.lot_code && !r.is_reversal && <LabelButton type="lot_label" id={l.lot_id} code={l.lot_code} compact />}
                    </li>
                  ))}
                  {r.line_count > r.lines.length && <li className="text-ink-muted">{t("rcv.moreLines", { count: r.line_count - r.lines.length })}</li>}
                </ul>
                {(r.inspections.length > 0 || (!r.is_reversal && !r.reversed && r.lines.some((l) => l.serial_count > 0))) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {r.inspections.map((i) => (
                      <Link
                        key={i.id}
                        to="/quality/inspection-orders"
                        search={{ status: "all", q: "", id: i.id }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-accent-ink hover:underline"
                      >
                        {t("rcv.inspection", { number: i.number })}
                        <Badge tone={i.status === "decided" ? "success" : i.status === "cancelled" ? "neutral" : "warning"}>
                          {tr(`rcv.inspectionStatus.${i.status}`)}
                        </Badge>
                      </Link>
                    ))}
                    {!r.is_reversal && !r.reversed && r.lines.some((l) => l.serial_count > 0) && (
                      <LabelButton type="receipt_serial_labels" id={r.id} code={r.number} text={t("serialList.receiptLabels")} />
                    )}
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
